import { inject, injectable } from "inversify";
import { MusicPlayer } from "@manager/scenarioRunner/musicManager/MusicPlayer";
import { TYPES } from "../../di/types";

interface VolumeMedia {
  volume: number;
}

interface VolumeTransition {
  time: number;
  targetVolume: number;
  duration: number;
}

export interface VolumeTransitionManager {
  baseVolume: number;

  /**
   * Only one transition can exist at during video time. If a transition is added at the same time as another transition,
   * the new transition will replace the old one.
   *
   * @param time
   * @param targetVolume
   * @param duration
   */
  addTransition(time: number, targetVolume: number, duration?: number): void;
  resetTransitions();
  updateTransitions(totalTime: number);
}

@injectable()
export class VolumeTransitionManagerImpl implements VolumeTransitionManager {
  baseVolume: number = 1;

  protected transitions: VolumeTransition[] = [];

  constructor(@inject(TYPES.music.MusicPlayer) protected media: VolumeMedia) {}

  addTransition(time: number, targetVolume: number, duration?: number): void {
    const existingIndex = this.transitions.findIndex((transition) => transition.time === time);

    if (existingIndex !== -1) {
      this.transitions[existingIndex] = { time, targetVolume, duration };
    } else {
      this.transitions.push({ time, targetVolume, duration });
    }

    this.transitions.sort((a, b) => a.time - b.time);
  }

  resetTransitions() {
    this.transitions = [];
  }

  updateTransitions(totalTime: number) {
    const curr = this.getActiveTransition(totalTime);
    const prev = this.getPreviousTransition(totalTime);

    if (curr) {
      const previousTransitionEndVolume = prev ? prev.targetVolume : this.baseVolume;

      this.media.volume = this.baseVolume * this.getTransitionVolume(totalTime, previousTransitionEndVolume, curr);
    } else if (prev) {
      this.media.volume = prev.targetVolume * this.baseVolume;
    } else {
      this.media.volume = this.baseVolume;
    }

    this.media.volume;
  }

  private getActiveTransition(time: number) {
    for (const transition of this.transitions) {
      const transitionEndTime = transition.time + transition.duration;

      if (time >= transition.time && time <= transitionEndTime) {
        return transition;
      }
    }

    return null;
  }

  private getPreviousTransition(time: number): VolumeTransition {
    const transitions = this.transitions;

    for (let i = transitions.length - 1; i >= 0; i--) {
      const transition = transitions[i];
      if (transition.time + transition.duration < time) {
        return transition;
      }
    }

    return null;
  }

  private getTransitionPercent(time: number, transition: VolumeTransition) {
    const fadeEndTime = transition.time + transition.duration;

    return (time - transition.time) / (fadeEndTime - transition.time);
  }

  private getTransitionVolume(time: number, startingVolume: number, transition: VolumeTransition) {
    return startingVolume + (transition.targetVolume - startingVolume) * this.getTransitionPercent(time, transition);
  }
}
